import { useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { currentSession, signOut, type SignedIn } from "./lib/auth";
import { SESSION_STALE, setErrorReportToken } from "./api/client";
import { SignIn } from "./routes/SignIn";
import { Companies } from "./routes/Companies";
import { CompanyPage } from "./routes/CompanyPage";
import { PersonPage } from "./routes/PersonPage";
import { PropertyPage } from "./routes/PropertyPage";
import { Styleguide } from "./routes/Styleguide";
import { People } from "./routes/People";
import { Properties } from "./routes/Properties";
import { Campaigns } from "./routes/Campaigns";
import { Home } from "./routes/Home";
import { MyCampaigns } from "./routes/MyCampaigns";
import { Ingestion } from "./routes/Ingestion";
import { Sources } from "./routes/Sources";
import { Jobs } from "./routes/Jobs";
import { CampaignBuilder } from "./routes/CampaignBuilder";
import { CampaignRunView } from "./routes/CampaignRunView";
import { CampaignPage } from "./routes/CampaignPage";
import { Import } from "./routes/Import";
import { SubmitUrl } from "./routes/SubmitUrl";
import { Enrichment } from "./routes/Enrichment";
import { Feedback } from "./components/Feedback";
import { Duplicates } from "./routes/Duplicates";
import { Alerts } from "./routes/Alerts";
import { Processing } from "./routes/Processing";
import { BrowserButton } from "./routes/BrowserButton";
import { CaptureRecords } from "./routes/CaptureRecords";
import { ImportRecords } from "./routes/ImportRecords";
import { ImportCompare } from "./routes/ImportCompare";
import { CaptureUpdateBanner } from "./components/CaptureUpdateBanner";
import { ServiceBanner } from "./components/ServiceBanner";
import { MainMenu } from "./components/MainMenu";
import { Spinner } from "./components/Spinner";

export function App() {
  const [session, setSession] = useState<SignedIn | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    currentSession().then((s) => { setSession(s); setChecking(false); });
  }, []);

  // A screen's error is reported as the person who saw it (api/client.ts).
  useEffect(() => { setErrorReportToken(session?.idToken ?? null); }, [session]);

  /* Keep the sign-in fresh.
   *
   * A Cognito id token lasts an hour. The token was read once when the app
   * loaded and then held in state, so after an hour every request came back
   * 401 and nothing here noticed: the app looked signed in and did nothing.
   * That is a demo breaking halfway through, not an edge case.
   *
   * getSession() renews from the stored refresh token when the one it has is
   * near expiry, so asking again is the whole fix. Twice an hour, and again
   * whenever the tab comes back, which is what covers a laptop that slept.
   *
   * Refreshing the token is not the same thing as refetching data, which stays
   * off on focus so a list cannot reorder under somebody mid-work. */
  useEffect(() => {
    const renew = () => {
      currentSession().then((s) => {
        // A null here means the refresh token is gone or revoked, which is a
        // real sign-out rather than a blip, and saying so beats leaving
        // somebody clicking buttons that quietly do nothing.
        if (s) setSession(s); else setSession(null);
      });
    };
    const every = window.setInterval(renew, 25 * 60 * 1000);
    window.addEventListener("focus", renew);
    /* focus does not fire when a laptop wakes with this tab already focused,
       and a background tab has its interval throttled, so the poll on Jobs or
       Ingestion can reach the API before the renewal does. visibilitychange
       covers the wake, and SESSION_STALE covers the request that got there
       first: api/client.ts fires it when PostgREST reports an expired token. */
    document.addEventListener("visibilitychange", renew);
    window.addEventListener(SESSION_STALE, renew);
    return () => {
      window.clearInterval(every);
      window.removeEventListener("focus", renew);
      document.removeEventListener("visibilitychange", renew);
      window.removeEventListener(SESSION_STALE, renew);
    };
  }, []);

  // Held rather than guessed. Rendering the sign-in screen while the check is
  // still running flashes a login form at somebody who is already signed in.
  if (checking) return <main><p className="muted"><Spinner />Checking your sign-in.</p></main>;
  if (!session) return <SignIn onSignedIn={setSession} />;

  return (
    <>
      <header className="topbar">
        <strong className="brand"><span className="brand-name">EVIE Sales</span></strong>
        <MainMenu email={session.email} role={session.role ?? null}
                  onSignOut={() => { signOut(); setSession(null); }} />
      </header>
      <ServiceBanner token={session.idToken} />
      <CaptureUpdateBanner />
      <Routes>
        <Route path="/companies" element={<Companies session={session} />} />
        {/* A record is a page, not a panel over the list. James, 2026-10-06. */}
        <Route path="/companies/:id" element={<CompanyPage session={session} />} />
        <Route path="/people" element={<People session={session} />} />
        <Route path="/people/:id" element={<PersonPage session={session} />} />
        <Route path="/properties" element={<Properties session={session} />} />
        <Route path="/properties/:id" element={<PropertyPage session={session} />} />
        <Route path="/duplicates" element={<Duplicates session={session} />} />
        {/* A person who owns a campaign opens on My campaigns.
            docs/plans/campaign-assignment.md, milestone 10. */}
        <Route path="/" element={<Home session={session} />} />
        <Route path="/my-campaigns" element={<MyCampaigns session={session} />} />
        <Route path="/campaigns" element={<Campaigns session={session} />} />
        <Route path="/campaigns/new" element={<CampaignBuilder session={session} />} />
        {/* A campaign is a page. docs/plans/campaigns-overhaul.md. */}
        <Route path="/campaigns/:name" element={<CampaignPage session={session} />} />
        <Route path="/runs/:runId" element={<CampaignRunView session={session} />} />
        <Route path="/import" element={<Import session={session} />} />
        {/* The records one import touched. Engine migration 230. */}
        <Route path="/imports/:id" element={<ImportRecords session={session} />} />
        <Route path="/imports/:id/compare" element={<ImportCompare session={session} />} />
        <Route path="/submit" element={<SubmitUrl session={session} />} />
        <Route path="/browser-button" element={<BrowserButton />} />
        <Route path="/captures/:id" element={<CaptureRecords session={session} />} />
        <Route path="/enrichment" element={<Enrichment session={session} />} />
        <Route path="/sources" element={<Sources session={session} />} />
        <Route path="/ingestion" element={<Ingestion session={session} />} />
        <Route path="/jobs" element={<Jobs session={session} />} />
        {/* Admin only. api.event is granted to saleshive_admin alone, so a
            sales user who typed the address would meet a refusal from the
            API. Sending them to Campaigns says so without the error. */}
        <Route path="/alerts" element={session.role === "saleshive_admin"
          ? <Alerts session={session} /> : <Navigate to="/campaigns" replace />} />
        {/* Admin only for the same reason: the processing reads (engine
            migration 164) are granted to saleshive_admin alone. */}
        <Route path="/processing" element={session.role === "saleshive_admin"
          ? <Processing session={session} /> : <Navigate to="/campaigns" replace />} />
        {/* The shared layer, every piece in every state, for an admin to
            review before a screen uses it. docs/plans/front-end-overhaul.md. */}
        <Route path="/styleguide" element={session.role === "saleshive_admin"
          ? <Styleguide /> : <Navigate to="/campaigns" replace />} />
        <Route path="*" element={<Navigate to="/campaigns" replace />} />
      </Routes>
      <Feedback token={session.idToken} email={session.email} />
    </>
  );
}
